// src/data/operatingCostsCatalog.ts
export interface OperatingCostTemplate {
  id: string;
  name: string;
  description: string;
  category: 'infrastructure' | 'facilities' | 'tools' | 'marketing' | 'sales' | 'legal' | 'insurance' | 'professional-services' | 'other';
  frequency: 'monthly' | 'quarterly' | 'yearly' | 'one-time';
  typicalRange: {
    min: number;
    max: number;
    unit: 'currency' | 'percentage';
  };
  tags: string[];
  examples?: string[];
}

// Comprehensive catalog of standard operating costs
export const OPERATING_COSTS_CATALOG: OperatingCostTemplate[] = [
  // Infrastructure
  {
    id: 'aws-hosting',
    name: 'AWS Cloud Hosting',
    description: 'Amazon Web Services hosting and compute costs',
    category: 'infrastructure',
    frequency: 'monthly',
    typicalRange: { min: 500, max: 50000, unit: 'currency' },
    tags: ['cloud', 'aws', 'hosting', 'scalability'],
    examples: ['EC2 instances', 'RDS databases', 'S3 storage', 'CloudFront CDN']
  },
  {
    id: 'google-cloud',
    name: 'Google Cloud Platform',
    description: 'Google Cloud hosting and services',
    category: 'infrastructure',
    frequency: 'monthly',
    typicalRange: { min: 300, max: 30000, unit: 'currency' },
    tags: ['cloud', 'gcp', 'hosting', 'scalability'],
    examples: ['Compute Engine', 'Cloud SQL', 'Cloud Storage', 'App Engine']
  },
  {
    id: 'azure-hosting',
    name: 'Microsoft Azure',
    description: 'Microsoft Azure cloud services',
    category: 'infrastructure',
    frequency: 'monthly',
    typicalRange: { min: 400, max: 40000, unit: 'currency' },
    tags: ['cloud', 'azure', 'hosting', 'enterprise'],
    examples: ['Virtual Machines', 'Azure SQL', 'Blob Storage', 'App Service']
  },
  {
    id: 'digital-ocean',
    name: 'DigitalOcean Droplets',
    description: 'DigitalOcean VPS hosting',
    category: 'infrastructure',
    frequency: 'monthly',
    typicalRange: { min: 50, max: 2000, unit: 'currency' },
    tags: ['cloud', 'vps', 'hosting', 'startup'],
    examples: ['Basic droplets', 'Managed databases', 'Load balancers']
  },
  {
    id: 'heroku-hosting',
    name: 'Heroku Platform',
    description: 'Heroku application hosting',
    category: 'infrastructure',
    frequency: 'monthly',
    typicalRange: { min: 25, max: 5000, unit: 'currency' },
    tags: ['paas', 'hosting', 'rapid-deployment'],
    examples: ['Dynos', 'Heroku Postgres', 'Redis', 'Add-ons']
  },

  // Facilities
  {
    id: 'office-rent',
    name: 'Office Rent',
    description: 'Monthly office space rental',
    category: 'facilities',
    frequency: 'monthly',
    typicalRange: { min: 2000, max: 50000, unit: 'currency' },
    tags: ['office', 'rent', 'facilities', 'fixed-cost'],
    examples: ['Coworking space', 'Private office', 'Meeting rooms']
  },
  {
    id: 'utilities-electricity',
    name: 'Electricity & Utilities',
    description: 'Electricity, water, internet, and other utilities',
    category: 'facilities',
    frequency: 'monthly',
    typicalRange: { min: 200, max: 2000, unit: 'currency' },
    tags: ['utilities', 'electricity', 'internet', 'facilities'],
    examples: ['Power bills', 'High-speed internet', 'Water/sewage']
  },
  {
    id: 'office-cleaning',
    name: 'Office Cleaning Services',
    description: 'Professional cleaning and maintenance',
    category: 'facilities',
    frequency: 'monthly',
    typicalRange: { min: 300, max: 2000, unit: 'currency' },
    tags: ['cleaning', 'maintenance', 'facilities'],
    examples: ['Daily cleaning', 'Restroom supplies', 'Trash removal']
  },
  {
    id: 'office-furniture',
    name: 'Office Furniture & Equipment',
    description: 'Desks, chairs, and office equipment',
    category: 'facilities',
    frequency: 'one-time',
    typicalRange: { min: 5000, max: 50000, unit: 'currency' },
    tags: ['furniture', 'equipment', 'setup', 'facilities'],
    examples: ['Desks and chairs', 'Conference tables', 'Storage cabinets']
  },

  // Tools & Software
  {
    id: 'development-tools',
    name: 'Development Tools & IDEs',
    description: 'Software development tools and licenses',
    category: 'tools',
    frequency: 'monthly',
    typicalRange: { min: 100, max: 2000, unit: 'currency' },
    tags: ['development', 'tools', 'software', 'licenses'],
    examples: ['VS Code', 'JetBrains IDEs', 'GitHub Pro', 'Docker']
  },
  {
    id: 'design-tools',
    name: 'Design Software',
    description: 'Graphic design and prototyping tools',
    category: 'tools',
    frequency: 'monthly',
    typicalRange: { min: 150, max: 1500, unit: 'currency' },
    tags: ['design', 'tools', 'creative', 'prototyping'],
    examples: ['Figma', 'Adobe Creative Suite', 'Sketch', 'InVision']
  },
  {
    id: 'project-management',
    name: 'Project Management Tools',
    description: 'Task management and collaboration software',
    category: 'tools',
    frequency: 'monthly',
    typicalRange: { min: 50, max: 500, unit: 'currency' },
    tags: ['project-management', 'collaboration', 'productivity'],
    examples: ['Asana', 'Trello', 'Jira', 'Monday.com']
  },
  {
    id: 'communication-tools',
    name: 'Communication & Collaboration',
    description: 'Team communication and video conferencing',
    category: 'tools',
    frequency: 'monthly',
    typicalRange: { min: 100, max: 1000, unit: 'currency' },
    tags: ['communication', 'video', 'collaboration'],
    examples: ['Slack', 'Zoom', 'Microsoft Teams', 'Google Workspace']
  },
  {
    id: 'analytics-tools',
    name: 'Analytics & Monitoring',
    description: 'Website analytics and performance monitoring',
    category: 'tools',
    frequency: 'monthly',
    typicalRange: { min: 50, max: 500, unit: 'currency' },
    tags: ['analytics', 'monitoring', 'performance'],
    examples: ['Google Analytics', 'Mixpanel', 'Sentry', 'DataDog']
  },

  // Marketing & Sales
  {
    id: 'content-marketing',
    name: 'Content Marketing',
    description: 'Blog posts, whitepapers, and content creation',
    category: 'marketing',
    frequency: 'monthly',
    typicalRange: { min: 2000, max: 15000, unit: 'currency' },
    tags: ['content', 'marketing', 'seo', 'brand'],
    examples: ['Blog writing', 'SEO services', 'Content strategy']
  },
  {
    id: 'social-media-ads',
    name: 'Social Media Advertising',
    description: 'Facebook, Instagram, LinkedIn, and Twitter ads',
    category: 'marketing',
    frequency: 'monthly',
    typicalRange: { min: 1000, max: 20000, unit: 'currency' },
    tags: ['social-media', 'advertising', 'paid-ads'],
    examples: ['Facebook Ads', 'LinkedIn campaigns', 'Twitter promotions']
  },
  {
    id: 'google-ads',
    name: 'Google Ads & Search',
    description: 'Google Ads and search engine marketing',
    category: 'marketing',
    frequency: 'monthly',
    typicalRange: { min: 1500, max: 25000, unit: 'currency' },
    tags: ['google-ads', 'sem', 'search', 'paid-search'],
    examples: ['Search campaigns', 'Display ads', 'YouTube ads']
  },
  {
    id: 'email-marketing',
    name: 'Email Marketing Platform',
    description: 'Email marketing and automation tools',
    category: 'marketing',
    frequency: 'monthly',
    typicalRange: { min: 50, max: 2000, unit: 'currency' },
    tags: ['email', 'marketing', 'automation', 'crm'],
    examples: ['Mailchimp', 'SendGrid', 'HubSpot', 'ActiveCampaign']
  },
  {
    id: 'pr-publicity',
    name: 'PR & Publicity',
    description: 'Public relations and media outreach',
    category: 'marketing',
    frequency: 'monthly',
    typicalRange: { min: 1000, max: 10000, unit: 'currency' },
    tags: ['pr', 'publicity', 'media', 'brand'],
    examples: ['Press releases', 'Media outreach', 'Influencer partnerships']
  },

  // Sales
  {
    id: 'crm-software',
    name: 'CRM Software',
    description: 'Customer relationship management system',
    category: 'sales',
    frequency: 'monthly',
    typicalRange: { min: 100, max: 2000, unit: 'currency' },
    tags: ['crm', 'sales', 'customer-management'],
    examples: ['HubSpot CRM', 'Salesforce', 'Pipedrive', 'Zoho CRM']
  },
  {
    id: 'sales-training',
    name: 'Sales Training & Development',
    description: 'Sales team training and development programs',
    category: 'sales',
    frequency: 'quarterly',
    typicalRange: { min: 2000, max: 10000, unit: 'currency' },
    tags: ['training', 'sales', 'development', 'coaching'],
    examples: ['Sales methodology training', 'Product knowledge', 'Negotiation skills']
  },
  {
    id: 'lead-generation',
    name: 'Lead Generation Services',
    description: 'Outbound lead generation and prospecting',
    category: 'sales',
    frequency: 'monthly',
    typicalRange: { min: 2000, max: 15000, unit: 'currency' },
    tags: ['leads', 'prospecting', 'outbound', 'sales'],
    examples: ['Cold outreach', 'Lead lists', 'Telemarketing services']
  },

  // Legal & Insurance
  {
    id: 'legal-services',
    name: 'Legal Services',
    description: 'Legal counsel and contract review',
    category: 'legal',
    frequency: 'monthly',
    typicalRange: { min: 1000, max: 10000, unit: 'currency' },
    tags: ['legal', 'contracts', 'compliance', 'counsel'],
    examples: ['Contract review', 'IP protection', 'Compliance consulting']
  },
  {
    id: 'business-insurance',
    name: 'Business Insurance',
    description: 'General liability, property, and cyber insurance',
    category: 'insurance',
    frequency: 'yearly',
    typicalRange: { min: 2000, max: 15000, unit: 'currency' },
    tags: ['insurance', 'liability', 'property', 'cyber'],
    examples: ['General liability', 'Property insurance', 'Cyber liability']
  },
  {
    id: 'professional-liability',
    name: 'Professional Liability Insurance',
    description: 'Errors and omissions insurance for professionals',
    category: 'insurance',
    frequency: 'yearly',
    typicalRange: { min: 1500, max: 8000, unit: 'currency' },
    tags: ['insurance', 'professional', 'liability', 'e&o'],
    examples: ['Tech E&O', 'Professional services insurance']
  },

  // Professional Services
  {
    id: 'accounting-services',
    name: 'Accounting & Bookkeeping',
    description: 'Financial accounting and bookkeeping services',
    category: 'professional-services',
    frequency: 'monthly',
    typicalRange: { min: 500, max: 5000, unit: 'currency' },
    tags: ['accounting', 'bookkeeping', 'finance', 'tax'],
    examples: ['Monthly bookkeeping', 'Financial reporting', 'Tax preparation']
  },
  {
    id: 'consulting-services',
    name: 'Consulting Services',
    description: 'External consultants and advisors',
    category: 'professional-services',
    frequency: 'monthly',
    typicalRange: { min: 2000, max: 20000, unit: 'currency' },
    tags: ['consulting', 'advisory', 'expertise'],
    examples: ['Strategy consulting', 'Technical consulting', 'Business development']
  },
  {
    id: 'hr-services',
    name: 'HR & Recruitment Services',
    description: 'Human resources and recruitment support',
    category: 'professional-services',
    frequency: 'monthly',
    typicalRange: { min: 1000, max: 8000, unit: 'currency' },
    tags: ['hr', 'recruitment', 'human-resources', 'talent'],
    examples: ['Recruitment services', 'HR consulting', 'Benefits administration']
  },

  // Other
  {
    id: 'office-supplies',
    name: 'Office Supplies & Materials',
    description: 'Office supplies, stationery, and materials',
    category: 'other',
    frequency: 'monthly',
    typicalRange: { min: 200, max: 1000, unit: 'currency' },
    tags: ['supplies', 'office', 'materials', 'stationery'],
    examples: ['Printer supplies', 'Coffee/break room', 'Office stationery']
  },
  {
    id: 'travel-entertainment',
    name: 'Travel & Entertainment',
    description: 'Business travel and client entertainment',
    category: 'other',
    frequency: 'monthly',
    typicalRange: { min: 500, max: 5000, unit: 'currency' },
    tags: ['travel', 'entertainment', 'business', 'client-relations'],
    examples: ['Flight tickets', 'Hotel stays', 'Client dinners', 'Conferences']
  },
  {
    id: 'banking-fees',
    name: 'Banking & Transaction Fees',
    description: 'Bank fees, payment processing, and transaction costs',
    category: 'other',
    frequency: 'monthly',
    typicalRange: { min: 100, max: 1000, unit: 'currency' },
    tags: ['banking', 'fees', 'payments', 'transactions'],
    examples: ['Bank account fees', 'Stripe/PayPal fees', 'Wire transfer fees']
  }
];

// Helper functions for working with the catalog
export const getCostsByCategory = (category: OperatingCostTemplate['category']) => {
  return OPERATING_COSTS_CATALOG.filter(cost => cost.category === category);
};

export const searchCosts = (query: string) => {
  const lowercaseQuery = query.toLowerCase();
  return OPERATING_COSTS_CATALOG.filter(cost =>
    cost.name.toLowerCase().includes(lowercaseQuery) ||
    cost.description.toLowerCase().includes(lowercaseQuery) ||
    cost.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery)) ||
    cost.category.toLowerCase().includes(lowercaseQuery)
  );
};

export const getCostById = (id: string) => {
  return OPERATING_COSTS_CATALOG.find(cost => cost.id === id);
};

export const getCostsByTags = (tags: string[]) => {
  return OPERATING_COSTS_CATALOG.filter(cost =>
    tags.some(tag => cost.tags.includes(tag))
  );
};